import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useDeliveryAddressContext } from '../contexts/DeliveryAddressContext';
import GeocodingService, { AddressComponents } from '../services/GeocodingService';

export function useGeocoding() {
  const { user } = useAuth();
  const { selectedAddress, fetchUserAddress } = useDeliveryAddressContext();
  const [isGeocodingUserAddress, setIsGeocodingUserAddress] = useState(false);

  /**
   * Automatically geocode user address if missing coordinates
   */
  const geocodeUserAddressIfNeeded = async (): Promise<boolean> => {
    if (!user || !selectedAddress) {
      return false;
    }

    // Check if coordinates are missing
    if (GeocodingService.areCoordinatesValid(selectedAddress.latitude, selectedAddress.longitude)) {
      return true; // Already has coordinates
    }

    // Check if we have enough address information to geocode
    if (!selectedAddress.street_address || !selectedAddress.city) {
      console.warn('[GEOCODING] Insufficient address information for geocoding');
      return false;
    }

    setIsGeocodingUserAddress(true);

    try {
      console.log('[GEOCODING] User address missing coordinates, attempting to geocode...');
      
      const addressComponents: AddressComponents = {
        street_address: selectedAddress.street_address,
        apartment: selectedAddress.apartment,
        city: selectedAddress.city,
        state: selectedAddress.state,
        zip_code: selectedAddress.zip_code,
        country: 'US' // Default to US
      };

      const success = await GeocodingService.geocodeAndUpdateUserAddress(user.id, addressComponents);
      
      if (success) {
        console.log('[GEOCODING] Successfully geocoded user address');
        // Refresh the address context to get updated coordinates
        await fetchUserAddress();
        return true;
      } else {
        console.warn('[GEOCODING] Failed to geocode user address');
        return false;
      }
    } catch (error) {
      console.error('[GEOCODING] Error geocoding user address:', error);
      return false;
    } finally {
      setIsGeocodingUserAddress(false);
    }
  };

  /**
   * Manually geocode a specific address
   */
  const geocodeAddress = async (address: string) => {
    try {
      return await GeocodingService.geocodeAddress(address);
    } catch (error) {
      console.error('[GEOCODING] Error geocoding address:', error);
      return null;
    }
  };

  /**
   * Update user address with coordinates
   */
  const updateUserAddressWithCoordinates = async (addressComponents: AddressComponents) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    setIsGeocodingUserAddress(true);

    try {
      const success = await GeocodingService.geocodeAndUpdateUserAddress(user.id, addressComponents);
      
      if (success) {
        // Refresh the address context
        await fetchUserAddress();
      }
      
      return success;
    } catch (error) {
      console.error('[GEOCODING] Error updating user address with coordinates:', error);
      throw error;
    } finally {
      setIsGeocodingUserAddress(false);
    }
  };

  /**
   * Batch geocode vendors (admin function)
   */
  const batchGeocodeVendors = async (limit: number = 10) => {
    try {
      return await GeocodingService.batchGeocodeVendors(limit);
    } catch (error) {
      console.error('[GEOCODING] Error batch geocoding vendors:', error);
      return 0;
    }
  };

  // Auto-geocode user address when it loads and is missing coordinates
  useEffect(() => {
    if (selectedAddress && user) {
      geocodeUserAddressIfNeeded();
    }
  }, [selectedAddress?.id, user?.id]);

  return {
    // States
    isGeocodingUserAddress,
    
    // Functions
    geocodeUserAddressIfNeeded,
    geocodeAddress,
    updateUserAddressWithCoordinates,
    batchGeocodeVendors,
    
    // Utilities
    areCoordinatesValid: GeocodingService.areCoordinatesValid
  };
}

export default useGeocoding;